/**
 * Concierge Detail Page - Concierge Service Details
 * 
 * Displays detailed information about a concierge account including
 * managed riads, users, and activity.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Briefcase, ArrowLeft, RefreshCw, Edit, Save, X,
  Building2, Users, Mail, Phone, MapPin, Activity,
  Plus, Trash2, CheckCircle, XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { api } from '../../App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const statusColors = {
  ACTIVE: 'bg-green-100 text-green-800',
  SUSPENDED: 'bg-orange-100 text-orange-800',
  CHURNED: 'bg-red-100 text-red-800'
};

const ConciergeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [concierge, setConcierge] = useState(null);
  const [managedRiads, setManagedRiads] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [availableRiads, setAvailableRiads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  // Dialogs
  const [addRiadDialog, setAddRiadDialog] = useState(false);
  const [removeRiadDialog, setRemoveRiadDialog] = useState({ open: false, riad: null });
  const [selectedRiadId, setSelectedRiadId] = useState('');

  useEffect(() => {
    fetchConciergeDetails();
  }, [id]);

  const fetchConciergeDetails = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/concierges/${id}`);
      setConcierge(data.concierge || data);
      setManagedRiads(data.managedRiads || data.managed_riads || []);
      setUsers(data.users || []);
      setEditForm({
        name: data.concierge?.name || data.name,
        email: data.concierge?.email || data.email,
        phone: data.concierge?.phone || data.phone,
        company: data.concierge?.company || data.company,
        notes: data.concierge?.notes || data.notes
      });
      
      // Fetch available riads (not yet managed by this concierge)
      fetchAvailableRiads();
      
      // Fetch audit logs
      fetchAuditLogs();
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/admin/concierges');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableRiads = async () => {
    try {
      const { data } = await api.get('/admin/riads?status=ACTIVE&limit=100');
      setAvailableRiads(data.riads);
    } catch (error) {
      console.error('Error fetching riads:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      // Fetch audit logs related to this concierge account
      const { data } = await api.get(`/admin/audit?limit=20`);
      // Filter logs related to this concierge
      const filtered = data.logs.filter(log => 
        log.description?.includes(id) || 
        log.entity_id === parseInt(id)
      );
      setAuditLogs(filtered.slice(0, 10));
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  const handleSave = async () => {
    try {
      await api.put(`/admin/customers/${id}`, editForm);
      toast.success('Conciergerie mise à jour');
      setEditing(false);
      fetchConciergeDetails();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleAddRiad = async () => {
    if (!selectedRiadId) return;
    
    try {
      await api.post('/admin/concierge-access', {
        concierge_account_id: parseInt(id),
        riad_id: parseInt(selectedRiadId)
      });
      toast.success('Riad ajouté');
      setAddRiadDialog(false);
      setSelectedRiadId('');
      fetchConciergeDetails();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleRemoveRiad = async () => {
    const riad = removeRiadDialog.riad;
    try {
      // Find the access ID for this riad
      const access = managedRiads.find(r => r.riad_id === riad.id || r.id === riad.id);
      if (access?.access_id) {
        await api.delete(`/admin/concierge-access/${access.access_id}`);
      } else {
        // Try to revoke by riad_id
        await api.post(`/admin/riads/${riad.id}/assign-concierge`, {
          concierge_account_id: null
        });
      }
      toast.success('Accès révoqué');
      setRemoveRiadDialog({ open: false, riad: null });
      fetchConciergeDetails();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!concierge) return null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/concierges')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{concierge.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={statusColors[concierge.status] || 'bg-gray-100'}>
                  {concierge.status}
                </Badge>
                <Badge variant="outline">Conciergerie</Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{managedRiads.length}</p>
              <p className="text-sm text-muted-foreground">Riads gérés</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{users.length}</p>
              <p className="text-sm text-muted-foreground">Utilisateurs</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {managedRiads.filter(r => r.status === 'ACTIVE').length}
              </p>
              <p className="text-sm text-muted-foreground">Accès actifs</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info" className="space-y-4">
        <TabsList>
          <TabsTrigger value="info" className="gap-2">
            <Briefcase className="w-4 h-4" />
            Informations
          </TabsTrigger>
          <TabsTrigger value="riads" className="gap-2">
            <Building2 className="w-4 h-4" />
            Riads gérés
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2">
            <Users className="w-4 h-4" />
            Utilisateurs
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-2">
            <Activity className="w-4 h-4" />
            Activité
          </TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Informations de la conciergerie</CardTitle>
              {editing ? (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                    <X className="w-4 h-4 mr-2" />
                    Annuler
                  </Button>
                  <Button size="sm" onClick={handleSave}>
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Nom de la conciergerie</Label>
                    {editing ? (
                      <Input
                        value={editForm.name}
                        onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                        className="mt-2"
                      />
                    ) : (
                      <p className="mt-1 font-medium">{concierge.name}</p>
                    )}
                  </div>
                  <div>
                    <Label>Société</Label>
                    {editing ? (
                      <Input
                        value={editForm.company || ''}
                        onChange={e => setEditForm({ ...editForm, company: e.target.value })}
                        className="mt-2"
                      />
                    ) : (
                      <p className="mt-1">{concierge.company || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label>Email</Label>
                    {editing ? (
                      <Input
                        value={editForm.email || ''}
                        onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                        className="mt-2"
                      />
                    ) : (
                      <p className="mt-1 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        {concierge.email || '-'}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Téléphone</Label>
                    {editing ? (
                      <Input
                        value={editForm.phone || ''}
                        onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                        className="mt-2"
                      />
                    ) : (
                      <p className="mt-1 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        {concierge.phone || '-'}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Notes</Label>
                    {editing ? (
                      <Textarea
                        value={editForm.notes || ''}
                        onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                        className="mt-2"
                        rows={3}
                      />
                    ) : (
                      <p className="mt-1 text-muted-foreground">{concierge.notes || 'Aucune note'}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Créé le</p>
                    <p className="font-medium">
                      {format(new Date(concierge.createdAt), 'dd/MM/yyyy', { locale: fr })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Type</p>
                    <p className="font-medium">{concierge.type}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">ID</p>
                    <p className="font-medium">{concierge.id}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Statut</p>
                    <Badge className={statusColors[concierge.status]}>
                      {concierge.status}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Riads Tab */}
        <TabsContent value="riads">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <div>
                <CardTitle>Riads gérés ({managedRiads.length})</CardTitle>
                <CardDescription>Riads auxquels cette conciergerie a accès</CardDescription>
              </div>
              <Button onClick={() => setAddRiadDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un riad
              </Button>
            </CardHeader>
            <CardContent>
              {managedRiads.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun riad géré</p>
                  <Button variant="outline" className="mt-4" onClick={() => setAddRiadDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un riad
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {managedRiads.map(riad => (
                    <div 
                      key={riad.id || riad.riad_id}
                      className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50"
                    >
                      <div 
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => navigate(`/admin/riads/${riad.riad_id || riad.id}`)}
                      >
                        {riad.logo ? (
                          <img src={riad.logo} alt={riad.name} className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-violet-600" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{riad.name || riad.riad?.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {riad.city || riad.riad?.city || riad.slug || riad.riad?.slug}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={riad.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                          {riad.status}
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setRemoveRiadDialog({ open: true, riad })}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>Utilisateurs ({users.length})</CardTitle>
              <CardDescription>Utilisateurs associés à cette conciergerie</CardDescription>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Aucun utilisateur</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Utilisateur</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(user => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{user.first_name} {user.last_name}</p>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{user.role || 'STAFF'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={user.active ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                            {user.active ? 'Actif' : 'Inactif'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Journal d'activité</CardTitle>
              <CardDescription>Dernières actions liées à cette conciergerie</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Aucune activité enregistrée</p>
              ) : (
                <div className="space-y-4">
                  {auditLogs.map(log => (
                    <div key={log.id} className="flex items-start gap-4 p-3 rounded-lg border">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                        <Activity className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{log.action}</p>
                        <p className="text-sm text-muted-foreground truncate">
                          {log.description || '-'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {log.user?.email || 'Système'} • {format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm', { locale: fr })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Riad Dialog */}
      <Dialog open={addRiadDialog} onOpenChange={setAddRiadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un riad</DialogTitle>
            <DialogDescription>
              Donnez accès à un riad pour cette conciergerie
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Sélectionner un riad</Label>
            <Select value={selectedRiadId} onValueChange={setSelectedRiadId}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Choisir un riad" />
              </SelectTrigger>
              <SelectContent>
                {availableRiads
                  .filter(r => !managedRiads.some(m => m.riad_id === r.id || m.id === r.id))
                  .map(riad => (
                    <SelectItem key={riad.id} value={String(riad.id)}>
                      {riad.name}
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddRiadDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddRiad} disabled={!selectedRiadId}>
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Riad Dialog */}
      <AlertDialog open={removeRiadDialog.open} onOpenChange={(open) => setRemoveRiadDialog({ open, riad: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Révoquer l'accès</AlertDialogTitle>
            <AlertDialogDescription>
              Voulez-vous révoquer l'accès de cette conciergerie au riad{' '}
              <strong>{removeRiadDialog.riad?.name}</strong> ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveRiad} className="bg-red-600 hover:bg-red-700">
              Révoquer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConciergeDetailPage;
